import { PaintBucket, Eye } from 'lucide-react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { PickColor } from 'src/pages/_myAccount/EditDecoration/components/PickColor'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/shared/components/ui/popover'
import { Input } from 'src/shared/components/ui/input'
import { TableCell } from 'src/shared/components/ui/table'
import { LineItemNotesField } from '../../../../shared/LineItemNotesPopover'
import { DecorationsMetaControl } from './DecorationsMetaControl'
import type { ProductRowDescriptionCellProps } from '../../types'

export const ProductRowDescriptionCell = ({
  quote_item,
  sorted_product_images,
  product_variant_color,
  product_variant_colors,
  isReadOnly,
  isEditMode,
  onOpenPreview,
  descriptionLocal,
  onDescriptionChange,
  colorPickerOpen,
  onColorPickerOpenChange,
  onSelectColor,
  onAddDecoration,
  onEditDecoration,
  onChangeDecoration,
  notesLocal,
  onNotesChange,
  t,
}: ProductRowDescriptionCellProps) => {
  return (
    <TableCell className="px-5 py-4 text-sm text-gray-700 align-top max-sm:pb-2">
      <Row className="items-start gap-3">
        <Column className="relative group shrink-0">
          <img
            src={sorted_product_images[0]?.image_url || ''}
            className="w-14 h-14 object-cover p-1.5 rounded-card-inner border border-border cursor-pointer transition-colors group-hover:opacity-40"
            onClick={() => onOpenPreview()}
            alt=""
          />
          <Eye className="w-5 h-5 text-white absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
        </Column>

        <Column className="gap-1.5 min-w-0">
          <Row className="items-center gap-2 min-w-0">
            {isEditMode ? (
              <Input
                variant="plain"
                value={descriptionLocal}
                onChange={(e) => onDescriptionChange(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="w-full truncate text-sm font-medium text-foreground"
              />
            ) : (
              <span className="min-w-0 truncate text-sm font-medium text-foreground max-sm:whitespace-normal">
                {t(quote_item.description ?? '')}
              </span>
            )}
          </Row>

          <Row className="!hidden md:!flex flex-wrap items-center gap-x-3 gap-y-1">
            {product_variant_color && product_variant_colors.length > 0 ? (
              <Popover
                open={colorPickerOpen}
                onOpenChange={(open) => {
                  if (!isReadOnly) onColorPickerOpenChange(open)
                }}
              >
                <PopoverTrigger>
                  <button
                    type="button"
                    disabled={isReadOnly}
                    className="flex w-fit items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 disabled:cursor-default"
                  >
                    <PaintBucket className="h-3 w-3 shrink-0 text-gray-500" />

                    <span className="whitespace-nowrap text-xs">
                      {product_variant_color.color}
                    </span>
                  </button>
                </PopoverTrigger>

                <PopoverContent className="w-[400px] p-4" align="start">
                  <Row className="mb-2 items-center gap-2">
                    <PaintBucket className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-medium">
                      {t('Choose color', 'Välj färg')}
                    </span>
                  </Row>

                  <PickColor
                    chosen_product_variant_color_={product_variant_color}
                    product_variant_colors={product_variant_colors}
                    setProduct_variant_color={async (color_id: number) => {
                      await onSelectColor(color_id)
                      onColorPickerOpenChange(false)
                    }}
                  />
                </PopoverContent>
              </Popover>
            ) : null}

            <DecorationsMetaControl
              quote_item={quote_item}
              isReadOnly={isReadOnly}
              onAddDecoration={onAddDecoration}
              onEditDecoration={onEditDecoration}
              onChangeDecoration={onChangeDecoration}
              t={t}
            />
          </Row>

          <LineItemNotesField
            value={notesLocal}
            onChange={onNotesChange}
            canEdit={!isReadOnly}
            readOnlyNote={quote_item.notes}
          />
        </Column>
      </Row>
    </TableCell>
  )
}
